import { WarningAmberRounded } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Download from "../../../../assets/images/svgs/downloadFileIcon.svg";
import Plus from "../../../../assets/images/svgs/plus.svg";
import Upload from "../../../../assets/images/svgs/uploadFileIcon.svg";
import AlertNotification from "../../../../components/common/Alert/Alert.tsx";
import BestPractisesButton from "../../../../components/common/BestPractisesButton/BestPractisesButton.tsx";
import BriaButton from "../../../../components/common/BriaButton/BriaButton.tsx";
import BriaButtonGroup from "../../../../components/common/BriaButtonGroup/BriaButtonGroup.tsx";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import ConfirmationPopup from "../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import FileUploader from "../../../../components/common/FileUploader/FileUploader";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores";
import EditorLayout from "../../../../layout/EditorLayout/EditorLayout";
import InputLayout from "../../../../layout/InputLayout/InputLayout";
import { isLegacyTgRoute } from "../../../NewTG/utils/tg-utils.ts";
import TgDatasetImageCard from "./ImageCard/TgDatasetImageCard";
import TgPrefixInput from "./Prefix/TgPrefixInput";
import styles from "./TgDatasetEditor.module.scss";

const TgDatasetEditor = () => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.editor" });
	const navigate = useSecureNavigate();
	const { uiStore, tgStore } = useAppStore();
	const { tgDatasetStore, tgModelStore } = tgStore;
	const { datasetId } = useParams();
	const isEditMode = !!datasetId;
	const [loadingUploadButton, setLoadingUploadButton] = useState(false);
	const [imageList, setImageList] = useState(tgDatasetStore.datasetForm.images);
	const [showGenerationPopup, setShowGenerationPopup] = useState(false);

	// Show popup when captions are still being generated (e.g., after page refresh)
	useEffect(() => {
		const startCheckingForCaptionCompletion = () => {
			const isCaptionUpdateInProgress = tgDatasetStore.datasetForm.captions_update_status === "in_progress";
			setShowGenerationPopup(isCaptionUpdateInProgress);

			if (isCaptionUpdateInProgress && tgDatasetStore.datasetForm.id) {
				tgDatasetStore.startPollingForCompletion(tgDatasetStore.datasetForm.id, () => {
					setShowGenerationPopup(false);
				});
			}
		};

		if (!tgDatasetStore.loadingRegenerate) {
			startCheckingForCaptionCompletion();
		}
	}, [tgDatasetStore.datasetForm.captions_update_status]);

	useEffect(() => {
		uiStore.hideSideBar();
	}, []);

	// Prevent images re-rendering multiple times and blinking
	useEffect(() => {
		setImageList((prevImages) => {
			const updatedImages = tgDatasetStore.datasetForm.images.map((img) => {
				const existingImage = prevImages.find((prevImg) => prevImg.id === img.skeleton_id);
				return existingImage ? { ...existingImage, ...img } : img; // Replace skeleton with the uploaded image
			});

			return updatedImages;
		});
	}, [tgDatasetStore.datasetForm.images]);

	const triggerOnChangeExisting = async (): Promise<boolean> => {
		if (tgModelStore.isDatasetFromExisting) {
			const duplicatedDataset = await tgDatasetStore.duplicateDataset(tgDatasetStore.datasetForm.id);
			tgDatasetStore.isFormDuplicateTriggered = true;
			tgDatasetStore.datasetForm = duplicatedDataset;
			tgModelStore.modelForm.dataset_id = duplicatedDataset.id;
			tgModelStore.isDatasetFromExisting = false;
			return true;
		} else {
			return false;
		}
	};

	const handleUploadImages = async (e: ChangeEvent<HTMLInputElement>) => {
		const files: File[] | null = Array.from(e.target.files || []);
		e.target.value = "";
		if (files.length) {
			setLoadingUploadButton(true);
			await triggerOnChangeExisting();
			setLoadingUploadButton(false);
			await tgDatasetStore.uploadImages(tgDatasetStore.datasetForm.id, files);
		}
	};

	const handleUpdateName = async (e: React.FocusEvent<HTMLInputElement>) => {
		const datasetName = e.target.value;
		await triggerOnChangeExisting();
		tgDatasetStore.handleFormChange("name", datasetName);
	};

	const saveDataset = async () => {
		await tgDatasetStore.updateDataset(tgDatasetStore.datasetForm.id, tgDatasetStore.datasetForm);
		const base = isLegacyTgRoute()
			? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
			: RouterConstants.NEW_TG.PROJECTS.fullPath;
		navigate(`${base}/${tgDatasetStore.datasetForm.project_id}/${RouterConstants.NEW_TG.DATASETS.path}`);
	};

	return (
		<EditorLayout
			className={styles.wrapper}
			headerClassName={styles.header}
			name={tgDatasetStore.datasetForm.name}
			placeholder={t("title")}
			onNameChange={(e) => tgDatasetStore.handleFormChange("name", e.target.value)}
			onNameBlur={handleUpdateName}
			primaryButton={
				<BriaButtonGroup>
					<FileUploader
						buttonProps={{ startIcon: <img src={Plus} /> }}
						buttonType="textMedium"
						buttonText={t("uploadBtn")}
						onUpload={handleUploadImages}
						maxFilesLimit={200 - tgDatasetStore.datasetForm.images.length}
						inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: true }}
						loading={loadingUploadButton}
						disabled={tgDatasetStore.loadingDuplicateDataset}
					/>
					{isEditMode && (
						<BriaButton
							buttonType="primaryMedium"
							onClick={saveDataset}
							loading={tgDatasetStore.loadingUpdateDataset}
							disabled={!tgDatasetStore.datasetForm.name?.trim()}
						>
							{t("primaryBtn")}
						</BriaButton>
					)}
				</BriaButtonGroup>
			}
			secondaryButton={<BestPractisesButton />}
			loading={
				!tgDatasetStore.datasetForm.id || (tgDatasetStore.loadingDataset && !tgDatasetStore.loadingRegenerate)
			}
			childrenClassName={styles.editorContent}
		>
			{tgDatasetStore.csvUploadErrors?.length > 0 && (
				<AlertNotification
					severity="error"
					icon={<WarningAmberRounded />}
					className={styles.errorMessage}
					action={
						<BriaButton
							className={styles.dismissButton}
							color="inherit"
							onClick={() => {
								tgDatasetStore.csvUploadErrors = [];
							}}
						>
							{t("dismiss")}
						</BriaButton>
					}
				>
					<ul>
						{tgDatasetStore.csvUploadErrors.map((error) => (
							<li>{error}</li>
						))}
					</ul>
				</AlertNotification>
			)}

			{tgDatasetStore.csvUploadWarnings?.length > 0 && (
				<AlertNotification
					className={styles.errorMessage}
					severity="warning"
					bgColor="#FFE083"
					action={
						<BriaButton
							className={styles.dismissButton}
							color="inherit"
							onClick={() => {
								tgDatasetStore.csvUploadWarnings = [];
							}}
						>
							{t("dismiss")}
						</BriaButton>
					}
					icon={<WarningAmberRounded />}
				>
					<ul>
						{tgDatasetStore.csvUploadWarnings.map((warning) => (
							<li>{warning}</li>
						))}
					</ul>
				</AlertNotification>
			)}
			<InputLayout className={styles.captionPrefix} label={t("form.prefix.label")} info={t("form.prefix.info")}>
				<TgPrefixInput {...{ triggerOnChangeExisting, setShowGenerationPopup }} />
			</InputLayout>
			{imageList.length > 0 && imageList.length < 10 && (
				<AlertNotification
					className={styles.errorMessage}
					severity="warning"
					bgColor="#FFE083"
					icon={<WarningAmberRounded />}
				>
					<Typography className={styles.warningMessageTitle}>{t("imagesLengthWarning.title")}</Typography>
					<Typography className={styles.warningMessage}>{t("imagesLengthWarning.description")}</Typography>
				</AlertNotification>
			)}
			<Box className={styles.imagesHeaderWrapper}>
				<Typography className={styles.imagesHeader}>{t("form.images.label")}</Typography>
				<BriaButtonGroup className={styles.csvButtons}>
					<BriaTooltip title={t("downloadCaptionsTooltip")}>
						<BriaButton
							buttonType="textMedium"
							onClick={async () => {
								await triggerOnChangeExisting();
								tgDatasetStore.downloadDatasetAsCSV(
									datasetId || String(tgDatasetStore.datasetForm.id),
									tgDatasetStore.datasetForm.name,
								);
							}}
							startIcon={<img src={Download} />}
							loading={tgDatasetStore.loadingDuplicateDataset}
							disabled={
								tgDatasetStore.loadingDuplicateDataset ||
								tgDatasetStore.loadingUpdateDataset ||
								tgDatasetStore.loadingUploadImages
							}
						>
							{t("downloadCaptions")}
						</BriaButton>
					</BriaTooltip>
					<BriaTooltip title={t("uploadCaptionsTooltip")}>
						<FileUploader
							buttonClassName={styles.csvUpload}
							buttonProps={{ startIcon: <img src={Upload} /> }}
							buttonType="textMedium"
							buttonText={t("uploadCaptions")}
							onUpload={async (e) => {
								await triggerOnChangeExisting();
								await tgDatasetStore.uploadCSVFile(
									e,
									datasetId || String(tgDatasetStore.datasetForm.id),
								);
							}}
							maxFilesLimit={1}
							inputProps={{ accept: "text/csv", multiple: false }}
							loading={tgDatasetStore.uploadingCSV}
							disabled={
								tgDatasetStore.loadingDuplicateDataset ||
								tgDatasetStore.loadingUpdateDataset ||
								tgDatasetStore.loadingUploadImages
							}
						/>
					</BriaTooltip>
				</BriaButtonGroup>
			</Box>
			<Box className={styles.images}>
				{imageList.map((image) => (
					<TgDatasetImageCard key={image.id} {...{ image, triggerOnChangeExisting }} />
				))}
			</Box>
			<ConfirmationPopup
				open={showGenerationPopup}
				onClose={() => setShowGenerationPopup(false)}
				title={t("form.prefix.generationPopup.title")}
				description={t("form.prefix.generationPopup.description")}
				confirmButtonText={t("form.prefix.generationPopup.button")}
				hideCancel={true}
				onClick={() => setShowGenerationPopup(false)}
				paperClassName={styles.popupContainer}
			/>
		</EditorLayout>
	);
};

export default observer(TgDatasetEditor);
